import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { 
  RefreshCw, 
  Globe, 
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  AlertTriangle,
  Target,
  Shield,
  BarChart3,
  Activity,
  Rocket,
  Brain,
  Landmark,
  DollarSign,
  Users,
  Calendar,
  Percent,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  XCircle,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TABS = [
  { id: "magicbowl", name: "Magic Bowl", icon: Sparkles },
  { id: "whales", name: "Options Whales", icon: DollarSign },
  { id: "overview", name: "Global Overview", icon: Globe },
  { id: "smartmoney", name: "Smart Money", icon: Landmark },
];

// Trade Card Component for detailed trade display
const TradeCard = ({ trade, onDismiss, formatCurrency, getVerdictColor }) => {
  const detectedTime = trade.detected_at ? new Date(trade.detected_at) : new Date();
  const timeAgo = Math.floor((Date.now() - detectedTime.getTime()) / (1000 * 60));
  const timeDisplay = timeAgo < 60 ? `${timeAgo}m ago` : `${Math.floor(timeAgo/60)}h ago`;
  
  return (
    <div className="p-4 bg-black/30 rounded border border-border hover:border-ai/50 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-lg">{trade.symbol}</span>
              <Badge className={trade.type === 'CALL' ? 'bg-profit text-black' : 'bg-loss'}>
                {trade.type}
              </Badge>
              {trade.conviction_pct && (
                <Badge className={trade.conviction_pct >= 75 ? 'bg-yellow-500 text-black' : 'bg-blue-500 text-white'}>
                  {trade.conviction_pct}% conviction
                </Badge>
              )}
              {trade.analysis?.is_sweep && (
                <Badge className="bg-warning text-black text-xs">SWEEP</Badge>
              )}
            </div>
            {trade.company_name && trade.company_name !== trade.symbol && (
              <p className="text-sm text-muted-foreground">{trade.company_name}</p>
            )}
            {trade.trade_description && (
              <p className="text-sm text-ai font-data mt-1">{trade.trade_description}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Detected: {detectedTime.toLocaleString()} ({timeDisplay})
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getVerdictColor(trade.analysis?.verdict)}>
            {trade.analysis?.success_probability}% 
          </Badge>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-8 w-8 p-0"
            onClick={() => onDismiss(trade.trade_id)}
            title="Dismiss for 48 hours"
          >
            <CheckCircle className="w-5 h-5 text-muted-foreground hover:text-profit" />
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-3">
        <div className="p-2 bg-black/20 rounded">
          <p className="text-xs text-muted-foreground">Strike</p>
          <p className="font-data font-bold">${trade.strike}</p>
        </div>
        <div className="p-2 bg-black/20 rounded">
          <p className="text-xs text-muted-foreground">Expiry</p>
          <p className="font-data">{trade.expiry_display || trade.expiry}</p>
          <p className="text-xs text-muted-foreground">{trade.days_to_expiry} days</p>
        </div>
        <div className="p-2 bg-black/20 rounded">
          <p className="text-xs text-muted-foreground">Contracts</p>
          <p className="font-data font-bold">{trade.volume?.toLocaleString()}</p>
        </div>
        <div className="p-2 bg-ai/20 rounded">
          <p className="text-xs text-muted-foreground">Premium</p>
          <p className="font-data font-bold text-ai">{formatCurrency(trade.premium)}</p>
        </div>
        <div className="p-2 bg-black/20 rounded">
          <p className="text-xs text-muted-foreground">Stock Price</p>
          <p className="font-data">${trade.current_stock_price?.toFixed(2)}</p>
        </div>
      </div>

      {/* Analysis */}
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className={
          trade.analysis?.risk_level === 'LOW' ? 'text-profit' :
          trade.analysis?.risk_level === 'EXTREME' ? 'text-loss' : 'text-warning'
        }>
          {trade.analysis?.risk_level} RISK
        </Badge>
        <Badge variant="outline" className="text-xs">
          {trade.analysis?.trade_type}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {trade.analysis?.sentiment}
        </span>
        {trade.analysis?.is_hedge && (
          <Badge variant="outline" className="text-ai text-xs">HEDGE</Badge>
        )}
      </div>

      {/* Reasoning */}
      {trade.analysis?.reasoning?.length > 0 && (
        <div className="text-xs text-muted-foreground space-y-1 border-t border-border pt-2 mt-2">
          {trade.analysis.reasoning.slice(0, 3).map((reason, i) => (
            <p key={i}>{reason}</p>
          ))}
        </div>
      )}

      {/* Algorithms Triggered */}
      {trade.analysis?.algorithms_triggered?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {trade.analysis.algorithms_triggered.map((algo, i) => (
            <Badge key={i} variant="secondary" className="text-xs bg-ai/10 text-ai">
              {algo.replace('_', ' ')}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

// NEW: Grouped Symbol Card - Shows all trades for one ticker consolidated
const GroupedSymbolCard = ({ group, formatCurrency, expandedSymbol, setExpandedSymbol }) => {
  const isExpanded = expandedSymbol === group.symbol;
  const totalContracts = group.call_contracts + group.put_contracts;
  
  return (
    <div className="border border-border rounded-lg overflow-hidden hover:border-ai/50 transition-all">
      {/* Header - Always visible */}
      <div 
        className="p-4 bg-black/40 cursor-pointer flex items-center justify-between"
        onClick={() => setExpandedSymbol(isExpanded ? null : group.symbol)}
      >
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-xl">{group.symbol}</span>
              <Badge className={
                group.sentiment === 'BULLISH' ? 'bg-profit text-black' :
                group.sentiment === 'BEARISH' ? 'bg-loss' : 'bg-muted'
              }>
                {group.sentiment}
              </Badge>
              {group.sweep_count > 0 && (
                <Badge className="bg-warning text-black text-xs">{group.sweep_count} SWEEPS</Badge>
              )}
              {group.high_conviction_count > 0 && (
                <Badge variant="outline" className="text-ai text-xs">{group.high_conviction_count} HIGH CONVICTION</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {group.trade_count} trades • {totalContracts.toLocaleString()} total contracts
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total Premium</p>
            <p className="font-data font-bold text-ai text-lg">{formatCurrency(group.total_premium)}</p>
          </div>
          <div className="flex gap-2 text-sm">
            <div className="text-center px-3 py-1 bg-profit/10 rounded">
              <p className="text-xs text-profit">CALLS</p>
              <p className="font-data text-profit">{formatCurrency(group.call_premium)}</p>
            </div>
            <div className="text-center px-3 py-1 bg-loss/10 rounded">
              <p className="text-xs text-loss">PUTS</p>
              <p className="font-data text-loss">{formatCurrency(group.put_premium)}</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>
      
      {/* Expanded Details - Individual trades */}
      {isExpanded && group.trades?.length > 0 && (
        <div className="p-4 bg-black/20 border-t border-border space-y-2">
          <p className="text-xs text-muted-foreground mb-2">Individual Trades (Top {group.trades.length}):</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border/50">
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Strike</th>
                  <th className="text-left p-2">Expiry</th>
                  <th className="text-right p-2">Contracts</th>
                  <th className="text-right p-2">Premium</th>
                  <th className="text-center p-2">IV</th>
                  <th className="text-center p-2">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {group.trades.map((trade, idx) => (
                  <tr key={trade.trade_id || idx} className="border-b border-border/30 hover:bg-white/5">
                    <td className="p-2">
                      <Badge className={trade.type === 'CALL' ? 'bg-profit/20 text-profit' : 'bg-loss/20 text-loss'}>
                        {trade.type}
                      </Badge>
                    </td>
                    <td className="p-2 font-data">${trade.strike}</td>
                    <td className="p-2">
                      <span className="font-data">{trade.expiry}</span>
                      <span className="text-xs text-muted-foreground ml-1">({trade.days_to_expiry}d)</span>
                    </td>
                    <td className="p-2 text-right font-data">{trade.volume?.toLocaleString()}</td>
                    <td className="p-2 text-right font-data text-ai">{formatCurrency(trade.premium)}</td>
                    <td className="p-2 text-center text-xs">{(trade.implied_volatility * 100).toFixed(0)}%</td>
                    <td className="p-2 text-center">
                      <Badge variant="outline" className="text-xs">
                        {trade.analysis?.verdict}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function FutureIsHere() {
  const [predictions, setPredictions] = useState(null);
  const [smartMoney, setSmartMoney] = useState(null);
  const [whaleOptions, setWhaleOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("magicbowl");
  const [expandedSymbol, setExpandedSymbol] = useState(null);  // NEW: For grouped view expansion
  
  // Magic Bowl state
  const [magicSymbol, setMagicSymbol] = useState("");
  const [magicResult, setMagicResult] = useState(null);
  const [magicLoading, setMagicLoading] = useState(false);

  const fetchPredictions = useCallback(async () => {
    try {
      const [predictionsRes, smartMoneyRes, whaleRes] = await Promise.all([
        api.get("/predictions/global"),
        api.get("/predictions/smart-money"),
        api.get("/options/whale-tracker")
      ]);
      setPredictions(predictionsRes.data);
      setSmartMoney(smartMoneyRes.data);
      setWhaleOptions(whaleRes.data);
    } catch (error) {
      console.error("Error fetching predictions:", error);
      toast.error("Failed to load predictions");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPredictions();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleDismissTrade = async (tradeId) => {
    try {
      await api.post(`/options/whale-tracker/dismiss/${tradeId}`);
      toast.success("Trade dismissed for 48 hours");
      // Remove from local state
      setWhaleOptions(prev => ({
        ...prev,
        whale_trades: prev.whale_trades?.filter(t => t.trade_id !== tradeId),
        sweeps: prev.sweeps?.filter(t => t.trade_id !== tradeId),
        high_conviction_trades: prev.high_conviction_trades?.filter(t => t.trade_id !== tradeId),
        mega_whales: prev.mega_whales?.filter(t => t.trade_id !== tradeId),
      }));
    } catch (error) {
      toast.error("Failed to dismiss trade");
    }
  };

  const handleMagicBowl = async (e) => {
    e.preventDefault();
    if (!magicSymbol.trim()) {
      toast.error("Enter a ticker symbol");
      return;
    }
    
    setMagicLoading(true);
    setMagicResult(null);
    
    try {
      const response = await api.post("/magic-bowl/analyze", { symbol: magicSymbol.trim() });
      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        setMagicResult(response.data);
        toast.success(`Analysis complete for ${magicSymbol.toUpperCase()}`);
      }
    } catch (error) {
      toast.error("Failed to analyze stock");
    } finally {
      setMagicLoading(false);
    }
  };

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const getVerdictColor = (verdict) => {
    if (!verdict) return "bg-muted";
    const v = verdict.toUpperCase();
    if (v.includes("BUY") || v.includes("HIGH") || v.includes("FAVORABLE")) return "bg-profit text-black";
    if (v.includes("SELL") || v.includes("AVOID") || v.includes("EXTREME")) return "bg-loss";
    if (v.includes("WAIT") || v.includes("NEUTRAL") || v.includes("RISKY")) return "bg-warning text-black";
    return "bg-muted";
  };

  const formatCurrency = (num) => {
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
    return `$${num.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="future-loading">
        <div className="h-48 skeleton rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-64 skeleton rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="future-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <Brain className="w-8 h-8 text-ai" />
            THE FUTURE IS HERE
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            AI predictions • Options whale tracking • Smart money signals
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh All
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          {TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="gap-2">
              <tab.icon className="w-4 h-4" />
              <span className="hidden md:inline">{tab.name}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Magic Bowl Tab */}
        <TabsContent value="magicbowl" className="mt-6 space-y-6">
          <Card className="card-dark border-ai/30">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-ai">
                <Sparkles className="w-6 h-6" />
                MAGIC BOWL - BUY / WAIT / HELL NO
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleMagicBowl} className="flex gap-2 mb-6">
                <Input
                  placeholder="Enter ticker (e.g., AAPL, TSLA)"
                  value={magicSymbol}
                  onChange={(e) => setMagicSymbol(e.target.value.toUpperCase())}
                  className="max-w-xs"
                  data-testid="magic-bowl-input"
                />
                <Button type="submit" disabled={magicLoading} className="bg-ai hover:bg-ai/80">
                  {magicLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyze
                    </>
                  )}
                </Button>
              </form>

              {magicResult && (
                <div className="space-y-4 animate-slide-in">
                  {/* Verdict */}
                  <div className="text-center p-6 bg-black/30 rounded">
                    <h3 className="font-heading text-2xl mb-2">{magicResult.symbol}</h3>
                    <p className="text-muted-foreground mb-4">{magicResult.name}</p>
                    <Badge className={`text-2xl px-6 py-2 ${getVerdictColor(magicResult.verdict)}`}>
                      {magicResult.verdict}
                    </Badge>
                    <p className="font-data text-3xl mt-4">${magicResult.current_price?.toFixed(2)}</p>
                  </div>

                  {/* Analysis */}
                  {magicResult.analysis && (
                    <div className="p-4 bg-ai/10 border border-ai/30 rounded">
                      <p className="text-sm">{magicResult.analysis}</p>
                    </div>
                  )}

                  {/* Scores */}
                  {magicResult.scores && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(magicResult.scores).map(([key, value]) => (
                        <div key={key} className="p-3 bg-black/30 rounded text-center">
                          <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                          <p className={`font-data text-xl ${value >= 60 ? 'text-profit' : value >= 40 ? 'text-warning' : 'text-loss'}`}>
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Options Whale Tracker Tab */}
        <TabsContent value="whales" className="mt-6 space-y-6">
          {whaleOptions?.is_loading ? (
            <Card className="card-dark">
              <CardContent className="p-8 text-center">
                <RefreshCw className="w-12 h-12 text-ai mx-auto mb-4 animate-spin" />
                <p className="text-lg">{whaleOptions?.message}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Scanning {whaleOptions?.scan_coverage || 50}+ symbols with 7 proprietary algorithms...
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="card-dark">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Overall Sentiment</p>
                    <Badge className={
                      whaleOptions?.summary?.overall_sentiment?.includes('BULLISH') ? 'bg-profit text-black text-lg px-4 py-1' :
                      whaleOptions?.summary?.overall_sentiment?.includes('BEARISH') ? 'bg-loss text-lg px-4 py-1' :
                      'bg-warning text-black text-lg px-4 py-1'
                    }>
                      {whaleOptions?.summary?.overall_sentiment || 'LOADING'}
                    </Badge>
                  </CardContent>
                </Card>
                
                <Card className="card-dark">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Call Flow</p>
                    <p className="font-data text-xl text-profit">
                      {formatCurrency(whaleOptions?.summary?.total_call_premium || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {whaleOptions?.summary?.total_call_contracts?.toLocaleString() || 0} contracts
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="card-dark">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Put Flow</p>
                    <p className="font-data text-xl text-loss">
                      {formatCurrency(whaleOptions?.summary?.total_put_premium || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {whaleOptions?.summary?.total_put_contracts?.toLocaleString() || 0} contracts
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">🚨 Sweeps</p>
                    <p className="font-data text-xl text-warning">
                      {whaleOptions?.summary?.sweep_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">aggressive entries</p>
                  </CardContent>
                </Card>
                
                <Card className="card-dark">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">🐋 Mega Whales</p>
                    <p className="font-data text-xl text-ai">
                      {whaleOptions?.summary?.mega_whale_count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">$1M+ trades</p>
                  </CardContent>
                </Card>
              </div>

              {/* Algorithms Used */}
              <div className="flex flex-wrap gap-2">
                {whaleOptions?.algorithms_used?.map((algo, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {algo.replace('_', ' ')}
                  </Badge>
                ))}
              </div>

              {/* Sweeps Alert Section */}
              {whaleOptions?.sweeps?.length > 0 && (
                <Card className="card-dark border-warning/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading flex items-center gap-2 text-warning">
                      <AlertTriangle className="w-5 h-5" />
                      🚨 SWEEP ALERTS - AGGRESSIVE ENTRIES ({whaleOptions.sweeps.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {whaleOptions.sweeps.slice(0, 10).map((trade, idx) => (
                        <TradeCard 
                          key={trade.trade_id || idx} 
                          trade={trade} 
                          onDismiss={handleDismissTrade}
                          formatCurrency={formatCurrency}
                          getVerdictColor={getVerdictColor}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* High Conviction Trades */}
              {whaleOptions?.high_conviction_trades?.length > 0 && (
                <Card className="card-dark border-profit/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading flex items-center gap-2 text-profit">
                      <Target className="w-5 h-5" />
                      HIGH CONVICTION TRADES ({whaleOptions.high_conviction_trades.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {whaleOptions.high_conviction_trades.map((trade, idx) => (
                        <TradeCard 
                          key={trade.trade_id || idx} 
                          trade={trade} 
                          onDismiss={handleDismissTrade}
                          formatCurrency={formatCurrency}
                          getVerdictColor={getVerdictColor}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Mega Whales */}
              {whaleOptions?.mega_whales?.length > 0 && (
                <Card className="card-dark border-ai/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading flex items-center gap-2 text-ai">
                      <DollarSign className="w-5 h-5" />
                      🐋 MEGA WHALE TRADES ($1M+)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {whaleOptions.mega_whales.slice(0, 15).map((trade, idx) => (
                        <TradeCard 
                          key={trade.trade_id || idx} 
                          trade={trade} 
                          onDismiss={handleDismissTrade}
                          formatCurrency={formatCurrency}
                          getVerdictColor={getVerdictColor}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* NEW: Grouped by Symbol - All trades consolidated by ticker */}
              {whaleOptions?.grouped_by_symbol?.length > 0 && (
                <Card className="card-dark border-profit/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Target className="w-5 h-5 text-profit" />
                      📊 GROUPED BY SYMBOL ({whaleOptions.summary?.unique_symbols || whaleOptions.grouped_by_symbol.length} stocks)
                      <Badge variant="outline" className="ml-2 text-xs">Click to expand</Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      All options activity consolidated by ticker - e.g., all Tesla plays in one section
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {whaleOptions.grouped_by_symbol.slice(0, 25).map((group, idx) => (
                        <GroupedSymbolCard 
                          key={group.symbol || idx} 
                          group={group}
                          formatCurrency={formatCurrency}
                          expandedSymbol={expandedSymbol}
                          setExpandedSymbol={setExpandedSymbol}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All Whale Trades Table */}
              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Activity className="w-5 h-5 text-ai" />
                    ALL FLOW ({whaleOptions?.whale_trades?.length || 0} trades)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-xs">
                          <th className="text-left p-2">Time</th>
                          <th className="text-left p-2">Symbol</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Strike</th>
                          <th className="text-left p-2">Expiry</th>
                          <th className="text-right p-2">Contracts</th>
                          <th className="text-right p-2">Premium</th>
                          <th className="text-center p-2">Conviction</th>
                          <th className="text-center p-2">Verdict</th>
                          <th className="text-center p-2">Sentiment</th>
                          <th className="text-center p-2">✓</th>
                        </tr>
                      </thead>
                      <tbody>
                        {whaleOptions?.whale_trades?.slice(0, 50).map((trade, idx) => (
                          <tr key={trade.trade_id || idx} className="border-b border-border/50 hover:bg-white/5">
                            <td className="p-2 text-xs text-muted-foreground">
                              {trade.detected_at ? new Date(trade.detected_at).toLocaleTimeString() : 'N/A'}
                            </td>
                            <td className="p-2">
                              <span className="font-heading font-bold">{trade.symbol}</span>
                              {trade.company_name && trade.company_name !== trade.symbol && (
                                <span className="text-xs text-muted-foreground block">{trade.company_name}</span>
                              )}
                            </td>
                            <td className="p-2">
                              <Badge className={trade.type === 'CALL' ? 'bg-profit/20 text-profit' : 'bg-loss/20 text-loss'}>
                                {trade.type}
                              </Badge>
                            </td>
                            <td className="p-2 font-data">${trade.strike}</td>
                            <td className="p-2">
                              <span className="font-data">{trade.expiry_display || trade.expiry}</span>
                              <span className="text-xs text-muted-foreground ml-1">({trade.days_to_expiry}d)</span>
                            </td>
                            <td className="p-2 text-right font-data">{trade.volume?.toLocaleString()}</td>
                            <td className="p-2 text-right font-data text-ai">{formatCurrency(trade.premium)}</td>
                            <td className="p-2 text-center">
                              <span className={`font-data font-bold ${trade.conviction_pct >= 75 ? 'text-yellow-400' : trade.conviction_pct >= 60 ? 'text-blue-400' : 'text-gray-400'}`}>
                                {trade.conviction_pct || trade.analysis?.institutional_likelihood || '?'}%
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              <Badge className={getVerdictColor(trade.analysis?.verdict)} variant="outline">
                                {trade.analysis?.verdict}
                              </Badge>
                            </td>
                            <td className="p-2 text-center text-xs">
                              {trade.analysis?.sentiment}
                            </td>
                            <td className="p-2 text-center">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0"
                                onClick={() => handleDismissTrade(trade.trade_id)}
                              >
                                <CheckCircle className="w-4 h-4 text-muted-foreground hover:text-profit" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {!whaleOptions?.whale_trades?.length && (
                    <div className="text-center py-8">
                      <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No whale trades detected</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Global Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {predictions?.global_markets?.map((market, idx) => (
              <Card key={idx} className="card-dark">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading font-bold">{market.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {market.region}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {market.change > 0 ? (
                      <TrendingUp className="w-4 h-4 text-profit" />
                    ) : market.change < 0 ? (
                      <TrendingDown className="w-4 h-4 text-loss" />
                    ) : (
                      <Minus className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className={`font-data ${market.change > 0 ? 'text-profit' : market.change < 0 ? 'text-loss' : ''}`}>
                      {market.change > 0 ? '+' : ''}{market.change?.toFixed(2)}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {predictions?.ai_outlook && (
            <Card className="card-dark border-ai/30">
              <CardHeader>
                <CardTitle className="font-heading text-ai">AI GLOBAL OUTLOOK</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{predictions.ai_outlook}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Smart Money Tab */}
        <TabsContent value="smartmoney" className="mt-6 space-y-6">
          {/* Alerts */}
          {smartMoney?.alerts?.length > 0 && (
            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  SMART MONEY ALERTS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {smartMoney.alerts.slice(0, 10).map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded border ${alert.type === 'URGENT' ? 'bg-loss/10 border-loss/30' : 'bg-white/5 border-border'}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={alert.type === 'URGENT' ? 'bg-loss' : 'bg-ai/20 text-ai'}>
                          {alert.source}
                        </Badge>
                        <span className="font-heading text-sm">{alert.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{alert.details}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Politician Trades */}
          {smartMoney?.politician_trades?.length > 0 && (
            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-ai" />
                  POLITICIAN TRADES
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {smartMoney.politician_trades.slice(0, 10).map((trade, idx) => (
                    <div key={idx} className="p-3 bg-white/5 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-heading">{trade.politician}</span>
                        <Badge variant="outline">{trade.party}</Badge>
                      </div>
                      <p className="text-sm">
                        <span className={trade.action === 'BUY' ? 'text-profit' : 'text-loss'}>
                          {trade.action}
                        </span> {trade.symbol} - {trade.amount}
                      </p>
                      <p className="text-xs text-muted-foreground">{trade.date}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
